import { create } from 'zustand';
import { fetchAuthSession, signOut as amplifySignOut } from 'aws-amplify/auth';

export type UserRole = 'parent' | 'student';
export type UserMode = 'high_school' | 'university';

interface AuthState {
  sub: string | null;
  /** For student accounts, their DynamoDB child_id (same as Cognito sub) */
  childId: string | null;
  displayName: string | null;
  role: UserRole | null;
  mode: UserMode | null;
  isLoading: boolean;
  isAuthenticated: boolean;
  /** Current streak count — set by pages after fetching weekly progress */
  streakCount: number;
  init: () => Promise<void>;
  setDisplayName: (name: string) => void;
  setStreakCount: (count: number) => void;
  signOut: () => Promise<void>;
}

// Prevent concurrent init() calls (e.g. LoginPage + main.tsx racing)
let initPromise: Promise<void> | null = null;

export function deriveRole(payload: Record<string, unknown>): UserRole | null {
  const customRole = payload['custom:role'] as string | undefined;
  if (customRole === 'student') return 'student';
  if (customRole === 'parent') return 'parent';

  // Legacy fallback for CHILD#-prefixed usernames
  const username = payload['cognito:username'] as string | undefined;
  if (username && username.startsWith('CHILD#')) return 'student';

  // Children created via admin_create_user have email: child-{uuid}@studywize.local
  const email = (payload['email'] as string | undefined) ?? '';
  if (email.startsWith('child-') && email.endsWith('@studywize.local')) return 'student';

  return 'parent';
}

/** Extract the DynamoDB child_id from the Cognito email alias used for child accounts. */
export function deriveChildId(payload: Record<string, unknown>, role: UserRole | null): string | null {
  if (role !== 'student') return null;
  const email = (payload['email'] as string | undefined) ?? '';
  if (email.startsWith('child-') && email.endsWith('@studywize.local')) {
    return email.slice('child-'.length, -'@studywize.local'.length);
  }
  // Fallback: sub is used as child_id for legacy CHILD# username accounts
  return (payload['sub'] as string | null) ?? null;
}

export function deriveMode(payload: Record<string, unknown>): UserMode | null {
  const customMode = payload['custom:mode'] as string | undefined;
  if (customMode === 'university') return 'university';
  if (customMode === 'high_school') return 'high_school';
  return null;
}

export const useAuthStore = create<AuthState>((set) => ({
  sub: null,
  childId: null,
  displayName: null,
  role: null,
  mode: null,
  isLoading: true,
  isAuthenticated: false,
  streakCount: 0,

  init: async () => {
    if (initPromise) return initPromise;
    initPromise = (async () => {
      try {
        const session = await fetchAuthSession();
        const payload = session.tokens?.idToken?.payload as Record<string, unknown> | undefined;
        if (payload?.sub) {
          const role = deriveRole(payload);
          const mode = deriveMode(payload);
          const sub = payload.sub as string;
          // Derive display name from JWT claims (Cognito standard + custom attributes)
          const displayName =
            (payload['name'] as string | undefined) ||
            (payload['given_name'] as string | undefined) ||
            null;
          document.documentElement.classList.toggle('dark', mode === 'university');
          set({
            sub,
            childId: deriveChildId(payload, role),
            displayName,
            role,
            mode,
            isAuthenticated: true,
            isLoading: false,
          });
        } else {
          set({ isLoading: false });
        }
      } catch {
        set({ isLoading: false });
      } finally {
        initPromise = null;
      }
    })();
    return initPromise;
  },

  setDisplayName: (displayName) => set({ displayName }),
  setStreakCount: (streakCount) => set({ streakCount }),

  signOut: async () => {
    await amplifySignOut();
    document.documentElement.classList.remove('dark');
    set({ sub: null, childId: null, displayName: null, role: null, mode: null, isAuthenticated: false, streakCount: 0 });
  },
}));
